import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    MenuItem,
    Pagination,
    Skeleton,
    Stack,
    Switch,
    TextField,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { GoogleMap, InfoWindow, LoadScript, Marker, MarkerClusterer } from '@react-google-maps/api';
import { site } from 'apis';
import googleMapsToolIcon from 'assets/google-maps-tool-icon.png';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getCountryFlagAlt, getCountryFlagSrc } from 'utils/countryFlags';
import { getGoogleMapsApiKey } from '../utils/googleMaps';
import SiteAvatarImage from './Components/SiteAvatarImage';

const ITEMS_PER_PAGE = 20;
const GREECE_CENTER = { lat: 39.0742, lng: 21.8243 };
const DEFAULT_GREECE_ZOOM = 7;
const mapContainerStyle = { width: '100%', height: '100%' };

type DisplayMode = 'list' | 'grid' | 'map';

interface SitePublic {
    id?: string;
    Id?: string;
    code?: string;
    Code?: string;
    name?: string;
    Name?: string;
    address1?: string;
    Address1?: string;
    address2?: string;
    Address2?: string;
    zipCode?: string;
    ZipCode?: string;
    city?: string;
    City?: string;
    countryAbbr?: string;
    CountryAbbr?: string;
    countryName?: string;
    CountryName?: string;
    contactName?: string;
    ContactName?: string;
    contactPhone?: string;
    ContactPhone?: string;
    contactEmail?: string;
    ContactEmail?: string;
    websiteUrl?: string;
    WebsiteUrl?: string;
    latitude?: string | number | null;
    Latitude?: string | number | null;
    longitude?: string | number | null;
    Longitude?: string | number | null;
    competitionCount?: number;
    CompetitionCount?: number;
}

interface SiteFilters {
    search: string;
    country: string;
    city: string;
}

const emptyFilters: SiteFilters = {
    search: '',
    country: '',
    city: ''
};

const normalizeText = (value: unknown) =>
    String(value || '')
        .trim()
        .toLowerCase();

const isUnknownLocationValue = (value: unknown) =>
    normalizeText(value) === '<άγνωστο>' || normalizeText(value) === '<unknown>' || normalizeText(value) === '<Ξ¬Ξ³Ξ½Ο‰ΟƒΟ„ΞΏ>';

const getDisplayValue = (value: unknown) => {
    const displayValue = String(value || '').trim();
    return displayValue && !isUnknownLocationValue(displayValue) ? displayValue : '';
};

const getFieldValue = (source: SitePublic, camelKey: keyof SitePublic, pascalKey: keyof SitePublic) =>
    source?.[camelKey] ?? source?.[pascalKey];

const getSiteId = (siteItem: SitePublic) => String(getFieldValue(siteItem, 'id', 'Id') || '');

const getSiteName = (siteItem: SitePublic, fallback: string) => getDisplayValue(getFieldValue(siteItem, 'name', 'Name')) || fallback;

const getSiteAddress = (siteItem: SitePublic) =>
    [
        [getFieldValue(siteItem, 'address1', 'Address1'), getFieldValue(siteItem, 'address2', 'Address2')]
            .map(getDisplayValue)
            .filter(Boolean)
            .join(' '),
        [getFieldValue(siteItem, 'zipCode', 'ZipCode'), getFieldValue(siteItem, 'city', 'City')]
            .map(getDisplayValue)
            .filter(Boolean)
            .join(' ')
    ]
        .filter(Boolean)
        .join(', ');

const toValidCoordinatePair = (latValue: unknown, lngValue: unknown) => {
    const lat = latValue === null || latValue === '' ? NaN : Number(latValue);
    const lng = lngValue === null || lngValue === '' ? NaN : Number(lngValue);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    if (lat === 0 && lng === 0) return null;

    return { lat, lng };
};

const getSiteCoordinates = (siteItem: SitePublic) =>
    toValidCoordinatePair(getFieldValue(siteItem, 'latitude', 'Latitude'), getFieldValue(siteItem, 'longitude', 'Longitude'));

const getWebsiteHref = (value?: string) => {
    if (!value) return '';
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const uniqueOptions = (items: SitePublic[], getter: (item: SitePublic) => string) =>
    Array.from(new Set(items.map(getter).filter(Boolean))).sort((a, b) => a.localeCompare(b));

const scrollToPageTop = () => {
    window.requestAnimationFrame(() => {
        const publicScrollContainer = document.getElementById('public-main-scroll-container');
        publicScrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    });
};

const createClusterStyle = (size: number, fill: string) => {
    const svg = encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
            <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 3}" fill="${fill}" stroke="#ffffff" stroke-width="3"/>
        </svg>
    `);

    return {
        url: `data:image/svg+xml;charset=UTF-8,${svg}`,
        height: size,
        width: size,
        textColor: '#ffffff',
        textSize: 14,
        fontWeight: '800',
        fontFamily: 'Arial, sans-serif'
    };
};

const createSelectedMarkerIcon = () => {
    if (!window.google?.maps) return undefined;

    const width = 44;
    const height = 58;
    const fill = '#ff6f61';
    const svg = encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
            <path d="M${width / 2} 1C${width * 0.28} 1 ${width * 0.12} ${height * 0.22} ${width * 0.12} ${height * 0.41}C${width * 0.12} ${
        height * 0.69
    } ${width / 2} ${height - 2} ${width / 2} ${height - 2}C${width / 2} ${height - 2} ${width * 0.88} ${height * 0.69} ${
        width * 0.88
    } ${height * 0.41}C${width * 0.88} ${height * 0.22} ${width * 0.72} 1 ${width / 2} 1Z" fill="${fill}" stroke="#ffffff" stroke-width="2"/>
            <circle cx="${width / 2}" cy="${height * 0.36}" r="8" fill="#ffffff"/>
        </svg>
    `);

    return {
        url: `data:image/svg+xml;charset=UTF-8,${svg}`,
        scaledSize: new window.google.maps.Size(width, height),
        anchor: new window.google.maps.Point(width / 2, height - 2)
    };
};

const clusterStyles = [createClusterStyle(44, '#ff2828'), createClusterStyle(52, '#a31a1a'), createClusterStyle(60, '#500e0e')];

const fullscreenButtonStyles = {
    position: 'absolute',
    left: 14,
    bottom: 34,
    zIndex: 10,
    width: 42,
    height: 42,
    background: '#fff',
    color: '#3c4043',
    borderRadius: 1,
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.22)',
    '&:hover': {
        background: '#f8f9fa'
    }
};

const SitesView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [displayMode, setDisplayMode] = useState<DisplayMode>('list');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filters, setFilters] = useState<SiteFilters>(emptyFilters);
    const [draftFilters, setDraftFilters] = useState<SiteFilters>(emptyFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeMarker, setActiveMarker] = useState<string | null>(null);
    const [selectedPosition, setSelectedPosition] = useState<{ lat: number; lng: number } | null>(null);
    const [clusterState, setClusterState] = useState(true);
    const [mapCenter, setMapCenter] = useState(GREECE_CENTER);
    const [isMapReady, setIsMapReady] = useState(false);
    const mapRef = useRef<google.maps.Map | null>(null);
    const mapShellRef = useRef<HTMLDivElement | null>(null);
    const googleMapsApiKey = useMemo(() => getGoogleMapsApiKey(), []);

    const sitesQuery = useQuery('publicSites', () => site.getPublicSites(), {
        suspense: false
    });

    const sites = useMemo(() => (Array.isArray(sitesQuery.data) ? (sitesQuery.data as SitePublic[]) : []), [sitesQuery.data]);
    const countries = useMemo(
        () =>
            uniqueOptions(
                sites,
                (siteItem) =>
                    getDisplayValue(getFieldValue(siteItem, 'countryName', 'CountryName')) ||
                    getDisplayValue(getFieldValue(siteItem, 'countryAbbr', 'CountryAbbr'))
            ),
        [sites]
    );
    const cities = useMemo(() => uniqueOptions(sites, (siteItem) => getDisplayValue(getFieldValue(siteItem, 'city', 'City'))), [sites]);

    const filteredSites = useMemo(() => {
        const search = normalizeText(filters.search);
        const country = normalizeText(filters.country);
        const city = normalizeText(filters.city);

        return sites.filter((siteItem) => {
            const countryValue =
                getDisplayValue(getFieldValue(siteItem, 'countryName', 'CountryName')) ||
                getDisplayValue(getFieldValue(siteItem, 'countryAbbr', 'CountryAbbr'));
            const cityValue = getDisplayValue(getFieldValue(siteItem, 'city', 'City'));
            const matchesSearch =
                !search ||
                [
                    getFieldValue(siteItem, 'name', 'Name'),
                    getFieldValue(siteItem, 'code', 'Code'),
                    getFieldValue(siteItem, 'address1', 'Address1'),
                    getFieldValue(siteItem, 'address2', 'Address2'),
                    getFieldValue(siteItem, 'zipCode', 'ZipCode'),
                    cityValue,
                    countryValue,
                    getFieldValue(siteItem, 'contactName', 'ContactName'),
                    getFieldValue(siteItem, 'contactEmail', 'ContactEmail'),
                    getFieldValue(siteItem, 'contactPhone', 'ContactPhone')
                ]
                    .map((value) => normalizeText(getDisplayValue(value)))
                    .some((value) => value.includes(search));
            const matchesCountry = !country || normalizeText(countryValue) === country;
            const matchesCity = !city || normalizeText(cityValue) === city;

            return matchesSearch && matchesCountry && matchesCity;
        });
    }, [sites, filters]);

    const mapSites = useMemo(
        () =>
            filteredSites
                .map((siteItem) => {
                    const coordinates = getSiteCoordinates(siteItem);
                    const id = getSiteId(siteItem);
                    if (!coordinates || !id) return null;

                    return {
                        ...siteItem,
                        id,
                        lat: coordinates.lat,
                        lng: coordinates.lng,
                        title: getSiteName(siteItem, t('Sites')),
                        address: getSiteAddress(siteItem)
                    };
                })
                .filter(Boolean) as Array<SitePublic & { id: string; lat: number; lng: number; title: string; address: string }>,
        [filteredSites, t]
    );

    const activeFilterCount = [filters.search, filters.country, filters.city].filter(Boolean).length;
    const totalPages = Math.max(1, Math.ceil(filteredSites.length / ITEMS_PER_PAGE));
    const pageStartIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedSites = filteredSites.slice(pageStartIndex, pageStartIndex + ITEMS_PER_PAGE);
    const showingStart = filteredSites.length ? pageStartIndex + 1 : 0;
    const showingEnd = Math.min(pageStartIndex + paginatedSites.length, filteredSites.length);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters, displayMode]);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    useEffect(() => {
        if (!activeMarker) return;
        if (!mapSites.some((siteItem) => siteItem.id === activeMarker)) {
            setActiveMarker(null);
            setSelectedPosition(null);
        }
    }, [activeMarker, mapSites]);

    const fitToPositions = (items: typeof mapSites) => {
        if (!mapRef.current || !window.google?.maps || !items.length) return false;

        const bounds = new window.google.maps.LatLngBounds();
        items.forEach((item) => bounds.extend({ lat: item.lat, lng: item.lng }));
        mapRef.current.fitBounds(bounds, 80);
        return true;
    };

    useEffect(() => {
        if (!isMapReady || !mapRef.current) return;

        if (selectedPosition) {
            mapRef.current.panTo(selectedPosition);
            mapRef.current.setZoom(15);
            return;
        }

        if (!fitToPositions(mapSites)) {
            mapRef.current.setCenter(GREECE_CENTER);
            mapRef.current.setZoom(DEFAULT_GREECE_ZOOM);
        }
    }, [isMapReady, mapSites, selectedPosition]);

    const handleOpenFilters = () => {
        setDraftFilters(filters);
        setIsFilterOpen(true);
    };

    const handleApplyFilters = () => {
        setFilters(draftFilters);
        setIsFilterOpen(false);
    };

    const handleClearFilters = () => {
        setDraftFilters(emptyFilters);
    };

    const handlePageChange = (_event: any, page: number) => {
        setCurrentPage(page);
        scrollToPageTop();
    };

    const handleOpenDetails = (siteItem: SitePublic) => {
        const id = getSiteId(siteItem);
        if (!id) return;

        navigate(`/public/sites/${id}`);
    };

    const handleShowInMap = (siteItem: SitePublic) => {
        const id = getSiteId(siteItem);
        if (!id || !getSiteCoordinates(siteItem)) return;

        navigate(`/public/sites/map/${id}`);
    };

    const stopCardClick = (event: any) => {
        event.stopPropagation();
    };

    const handleMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
        setIsMapReady(true);

        if (!fitToPositions(mapSites)) {
            map.setCenter(GREECE_CENTER);
            map.setZoom(DEFAULT_GREECE_ZOOM);
        }
    };

    const handleToggleFullscreen = () => {
        const mapShell = mapShellRef.current as (HTMLDivElement & { webkitRequestFullscreen?: () => Promise<void> }) | null;
        const documentElement = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => Promise<void> };

        if (document.fullscreenElement || documentElement.webkitFullscreenElement) {
            (document.exitFullscreen || documentElement.webkitExitFullscreen)?.call(document);
            return;
        }

        (mapShell?.requestFullscreen || mapShell?.webkitRequestFullscreen)?.call(mapShell);
    };

    const renderMapIcon = () => (
        <Box component="img" src={googleMapsToolIcon} alt="Google Maps" sx={{ width: 24, height: 24, flexShrink: 0 }} />
    );

    const renderSiteImage = (siteItem: SitePublic, variant: 'list' | 'grid' | 'map') => (
        <SiteAvatarImage
            siteId={getSiteId(siteItem)}
            alt={getSiteName(siteItem, t('Sites'))}
            iconSize={variant === 'map' ? 42 : 56}
            sx={{
                width: variant === 'list' ? { xs: '100%', md: 230 } : '100%',
                height: variant === 'map' ? 140 : 160,
                minHeight: variant === 'map' ? 140 : 160,
                borderRadius: variant === 'map' ? 1.5 : 2
            }}
        />
    );

    const renderMetaChips = (siteItem: SitePublic) => {
        const flagSrc = getCountryFlagSrc(siteItem);
        const countryAbbr = getDisplayValue(getFieldValue(siteItem, 'countryAbbr', 'CountryAbbr'));
        const competitionCount = Number(getFieldValue(siteItem, 'competitionCount', 'CompetitionCount') || 0);

        return (
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
                {flagSrc && (
                    <Box
                        component="img"
                        src={flagSrc}
                        alt={getCountryFlagAlt(siteItem)}
                        sx={{
                            width: 28,
                            height: 20,
                            objectFit: 'cover',
                            border: '1px solid #d7e0ea',
                            borderRadius: 0.5
                        }}
                    />
                )}
                {countryAbbr && <Chip size="small" label={countryAbbr} sx={{ fontWeight: 800, background: '#ffe3ec', color: '#b01249' }} />}
                <Chip
                    size="small"
                    label={`${competitionCount} ${t('Competitions')}`}
                    sx={{ fontWeight: 800, background: '#e8f3ff', color: '#075aaa' }}
                />
            </Stack>
        );
    };

    const renderContactBlock = (siteItem: SitePublic) => {
        const phone = getDisplayValue(getFieldValue(siteItem, 'contactPhone', 'ContactPhone'));
        const email = getDisplayValue(getFieldValue(siteItem, 'contactEmail', 'ContactEmail'));
        const websiteUrl = getDisplayValue(getFieldValue(siteItem, 'websiteUrl', 'WebsiteUrl'));

        return (
            <Stack spacing={0.5}>
                {phone && (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: '#4d5f70' }}>
                        <LocalPhoneOutlinedIcon sx={{ fontSize: 18 }} />
                        <Typography variant="body2">{phone}</Typography>
                    </Stack>
                )}
                {email && (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <EmailOutlinedIcon sx={{ fontSize: 18, color: '#4d5f70' }} />
                        <Typography
                            component="a"
                            href={`mailto:${email}`}
                            onClick={stopCardClick}
                            variant="body2"
                            sx={{
                                color: '#075aaa',
                                fontWeight: 700,
                                textDecoration: 'none',
                                overflowWrap: 'anywhere',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            {email}
                        </Typography>
                    </Stack>
                )}
                {websiteUrl && (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <LanguageOutlinedIcon sx={{ fontSize: 18, color: '#4d5f70' }} />
                        <Typography
                            component="a"
                            href={getWebsiteHref(websiteUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={stopCardClick}
                            variant="body2"
                            sx={{
                                color: '#075aaa',
                                fontWeight: 700,
                                textDecoration: 'none',
                                overflowWrap: 'anywhere',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            {websiteUrl}
                        </Typography>
                    </Stack>
                )}
            </Stack>
        );
    };

    const renderLocationBlock = (siteItem: SitePublic, variant: 'list' | 'grid' | 'map') => {
        const hasMap = Boolean(getSiteCoordinates(siteItem));
        const address = getSiteAddress(siteItem);

        if (!address) return null;

        return (
            <Stack
                direction="row"
                spacing={0.75}
                alignItems="flex-start"
                onClick={(event) => {
                    if (!hasMap) return;
                    stopCardClick(event);
                    handleShowInMap(siteItem);
                }}
                sx={{
                    color: variant === 'list' ? '#4d5f70' : 'text.secondary',
                    cursor: hasMap ? 'pointer' : 'default',
                    borderRadius: 1.5,
                    width: 'fit-content',
                    maxWidth: '100%',
                    pr: 1,
                    '&:hover': hasMap ? { background: '#eef7ff', color: '#075aaa' } : undefined
                }}
            >
                <LocationOnOutlinedIcon sx={{ fontSize: variant === 'list' ? 20 : 18, mt: 0.2, flexShrink: 0 }} />
                <Typography variant={variant === 'list' ? 'body1' : 'body2'}>
                    {address}
                </Typography>
                {hasMap && renderMapIcon()}
            </Stack>
        );
    };

    const renderListCard = (siteItem: SitePublic) => (
        <Box
            key={getSiteId(siteItem)}
            onClick={() => handleOpenDetails(siteItem)}
            sx={{
                p: { xs: 2, md: 2.5 },
                border: '1px solid #d9e6f2',
                borderRadius: 3,
                background: '#fff',
                boxShadow: '0 8px 24px rgba(36, 76, 118, 0.08)',
                cursor: 'pointer',
                transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 32px rgba(36, 76, 118, 0.16)',
                    borderColor: '#7db7ef'
                }
            }}
        >
            <Grid container spacing={2.5}>
                <Grid item xs={12} md="auto">
                    {renderSiteImage(siteItem, 'list')}
                </Grid>
                <Grid item xs={12} md>
                    <Stack spacing={1.25}>
                        {renderMetaChips(siteItem)}
                        <Typography variant="h3" sx={{ color: '#003b73', fontWeight: 800, lineHeight: 1.2 }}>
                            {getSiteName(siteItem, t('Sites'))}
                        </Typography>
                        {renderLocationBlock(siteItem, 'list')}
                        {renderContactBlock(siteItem)}
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );

    const renderImageCard = (siteItem: SitePublic) => (
        <Grid item xs={12} sm={6} lg={4} key={getSiteId(siteItem)}>
            <Box
                onClick={() => handleOpenDetails(siteItem)}
                sx={{
                    p: 1.5,
                    height: '100%',
                    borderRadius: 3,
                    background: '#fff',
                    border: '1px solid #d9e6f2',
                    boxShadow: '0 8px 24px rgba(36, 76, 118, 0.08)',
                    cursor: 'pointer',
                    transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 32px rgba(36, 76, 118, 0.16)',
                        borderColor: '#7db7ef'
                    }
                }}
            >
                {renderSiteImage(siteItem, 'grid')}
                <Stack spacing={1} sx={{ pt: 1.5 }}>
                    {renderMetaChips(siteItem)}
                    <Typography variant="h4" sx={{ color: '#003b73', fontWeight: 800 }}>
                        {getSiteName(siteItem, t('Sites'))}
                    </Typography>
                    {renderLocationBlock(siteItem, 'grid')}
                    {renderContactBlock(siteItem)}
                </Stack>
            </Box>
        </Grid>
    );

    const renderMapList = () => (
        <Box
            sx={{
                height: '100%',
                overflowY: 'auto',
                background: '#fff',
                scrollbarWidth: 'thin',
                scrollbarColor: '#9aa8b6 #eef2f6',
                '&::-webkit-scrollbar': { width: 10 },
                '&::-webkit-scrollbar-track': { background: '#eef2f6' },
                '&::-webkit-scrollbar-thumb': {
                    background: '#9aa8b6',
                    borderRadius: 10,
                    border: '2px solid #eef2f6'
                }
            }}
        >
            {sitesQuery.isLoading && (
                <Stack spacing={1.5} sx={{ p: 1.5 }}>
                    {[1, 2, 3].map((item) => (
                        <Skeleton key={item} variant="rounded" height={172} />
                    ))}
                </Stack>
            )}

            {!sitesQuery.isLoading && mapSites.length > 0
                ? mapSites.map((siteItem) => (
                      <Box
                          key={siteItem.id}
                          onClick={() => handleOpenDetails(siteItem)}
                          sx={{
                              display: 'grid',
                              gridTemplateColumns: '112px 1fr',
                              gap: 1.5,
                              p: 1.5,
                              borderBottom: '1px solid #e5eaf0',
                              cursor: 'pointer',
                              background: activeMarker === siteItem.id ? '#eef7ff' : '#fff',
                              boxShadow: activeMarker === siteItem.id ? 'inset 3px 0 0 #1976d2' : 'none',
                              transition: 'background 160ms ease, box-shadow 160ms ease',
                              '&:hover': {
                                  background: '#f5faff',
                                  boxShadow: 'inset 3px 0 0 #1976d2'
                              }
                          }}
                      >
                          {renderSiteImage(siteItem, 'map')}
                          <Stack spacing={0.75} sx={{ minWidth: 0, ml: 0.75 }}>
                              <Typography sx={{ color: '#006ce4', fontWeight: 900, lineHeight: 1.25, fontSize: 16 }}>
                                  {siteItem.title}
                              </Typography>
                              {siteItem.address && (
                                  <Stack
                                      direction="row"
                                      spacing={0.5}
                                      alignItems="flex-start"
                                      onClick={(event) => {
                                          event.stopPropagation();
                                          navigate(`/public/sites/map/${siteItem.id}`);
                                      }}
                                      sx={{
                                          color: '#4b5563',
                                          cursor: 'zoom-in',
                                          borderRadius: 1,
                                          '&:hover': {
                                              color: '#075aaa',
                                              background: '#eef6ff'
                                          }
                                      }}
                                  >
                                      <LocationOnOutlinedIcon sx={{ fontSize: 17, mt: 0.2, flexShrink: 0 }} />
                                      <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                                          {siteItem.address}
                                      </Typography>
                                  </Stack>
                              )}
                          </Stack>
                      </Box>
                  ))
                : !sitesQuery.isLoading && (
                      <Box sx={{ p: 3 }}>
                          <Typography sx={{ fontWeight: 800 }}>{t('NoDataFound')}</Typography>
                          <Typography variant="body2" color="text.secondary">
                              {t('NoMapCoordinatesFound', { defaultValue: 'No locations with map coordinates found.' })}
                          </Typography>
                      </Box>
                  )}
        </Box>
    );

    const renderMap = () => {
        if (!googleMapsApiKey) {
            return (
                <Box sx={{ p: 5, textAlign: 'center', borderRadius: 3, background: '#fff', border: '1px solid #e3e8ef' }}>
                    <Typography variant="h3" sx={{ mb: 1 }}>
                        {t('Map')}
                    </Typography>
                    <Typography color="text.secondary">
                        {t('GoogleMapsApiKeyNotConfigured', { defaultValue: 'Google Maps API key is not configured.' })}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box
                sx={{
                    position: 'relative',
                    height: { xs: 560, md: 640 },
                    overflow: 'hidden',
                    background: '#fff',
                    borderRadius: 3,
                    border: '1px solid #d9e6f2',
                    boxShadow: '0 8px 24px rgba(36, 76, 118, 0.08)'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%', width: '100%', background: '#fff' }}>
                    <Box
                        sx={{
                            width: { xs: '100%', md: 360, lg: 390 },
                            minWidth: { md: 360, lg: 390 },
                            height: { xs: '44%', md: '100%' },
                            minHeight: { xs: 280, md: 0 },
                            overflow: 'hidden',
                            borderRight: { md: '1px solid #d9e3ee' },
                            borderBottom: { xs: '1px solid #d9e3ee', md: 0 },
                            background: '#fff',
                            zIndex: 2
                        }}
                    >
                        {renderMapList()}
                    </Box>
                    <Box
                        ref={mapShellRef}
                        sx={{
                            position: 'relative',
                            flex: 1,
                            height: { xs: '56%', md: '100%' },
                            minWidth: 0,
                            minHeight: { xs: 340, md: 0 },
                            background: '#fff'
                        }}
                    >
                        <Box sx={{ height: '100%', width: '100%' }}>
                            <LoadScript googleMapsApiKey={googleMapsApiKey} language="el" region="GR">
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={selectedPosition || mapCenter}
                                    zoom={selectedPosition ? 15 : DEFAULT_GREECE_ZOOM}
                                    onLoad={handleMapLoad}
                                    options={{ fullscreenControl: false, mapTypeControl: false, streetViewControl: false }}
                                >
                                    <MarkerClusterer
                                        key={clusterState ? 8 : 0}
                                        averageCenter
                                        gridSize={60}
                                        minimumClusterSize={2}
                                        maxZoom={clusterState ? 8 : 0}
                                        styles={clusterStyles}
                                    >
                                        {(clusterer) => (
                                            <>
                                                {mapSites.map((siteItem) => (
                                                    <Marker
                                                        key={siteItem.id}
                                                        position={{ lat: siteItem.lat, lng: siteItem.lng }}
                                                        clusterer={clusterer}
                                                        icon={activeMarker === siteItem.id ? createSelectedMarkerIcon() : undefined}
                                                        onClick={() => {
                                                            setActiveMarker(siteItem.id);
                                                            setSelectedPosition({ lat: siteItem.lat, lng: siteItem.lng });
                                                        }}
                                                    >
                                                        {activeMarker === siteItem.id && (
                                                            <InfoWindow onCloseClick={() => setActiveMarker(null)} options={{ maxWidth: 280 }}>
                                                                <Box
                                                                    sx={{
                                                                        width: 240,
                                                                        maxWidth: 240,
                                                                        overflow: 'hidden',
                                                                        pr: 0.5,
                                                                        '& p': {
                                                                            m: 0,
                                                                            overflowWrap: 'anywhere',
                                                                            wordBreak: 'break-word'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Box
                                                                        component="p"
                                                                        onClick={() => handleOpenDetails(siteItem)}
                                                                        sx={{
                                                                            cursor: 'pointer',
                                                                            fontWeight: 'bold',
                                                                            mb: 0.25,
                                                                            '&:hover': {
                                                                                textDecoration: 'underline'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {siteItem.title}
                                                                    </Box>
                                                                    <p>{siteItem.address}</p>
                                                                </Box>
                                                            </InfoWindow>
                                                        )}
                                                    </Marker>
                                                ))}
                                            </>
                                        )}
                                    </MarkerClusterer>
                                </GoogleMap>
                            </LoadScript>
                        </Box>

                        {!activeMarker && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    zIndex: 12,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    background: 'rgba(255,255,255,0.94)',
                                    border: '1px solid #d9e3ee',
                                    borderRadius: 2,
                                    px: 1.25,
                                    py: 0.5,
                                    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.16)'
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                                    {t('ClusterMarkers', { defaultValue: 'Cluster markers' })}
                                </Typography>
                                <Switch
                                    checked={clusterState}
                                    onChange={(event) => setClusterState(event.target.checked)}
                                    size="small"
                                    inputProps={{ 'aria-label': 'cluster markers' }}
                                />
                            </Box>
                        )}

                        {!mapSites.length && !sitesQuery.isLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 24,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    zIndex: 12,
                                    background: '#fff',
                                    border: '1px solid #d9e3ee',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1,
                                    boxShadow: '0 6px 18px rgba(15,23,42,0.12)'
                                }}
                            >
                                <Typography sx={{ fontWeight: 800 }}>
                                    {t('NoMapCoordinatesFound', { defaultValue: 'No locations with map coordinates found.' })}
                                </Typography>
                            </Box>
                        )}
                        <IconButton aria-label={t('FullscreenMap')} onClick={handleToggleFullscreen} sx={fullscreenButtonStyles}>
                            <FullscreenIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ width: '100%', px: { xs: 2, md: 5 }, py: 3 }}>
            <Stack spacing={2.5} sx={{ maxWidth: 1280, mx: 'auto' }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    spacing={2}
                >
                    <Box>
                        <Typography variant="h2" sx={{ fontWeight: 900, color: '#111827' }}>
                            {t('Sites')}
                        </Typography>
                        {!sitesQuery.isLoading && displayMode !== 'map' && filteredSites.length > 0 && (
                            <Typography variant="body2" sx={{ mt: 0.5, color: '#64748b', fontWeight: 600 }}>
                                {t('ShowingRange', { defaultValue: 'Showing {{start}}-{{end}}', start: showingStart, end: showingEnd })} /{' '}
                                {filteredSites.length}
                            </Typography>
                        )}
                        {!sitesQuery.isLoading && displayMode === 'map' && (
                            <Typography variant="body2" sx={{ mt: 0.5, color: '#64748b', fontWeight: 600 }}>
                                {mapSites.length} / {filteredSites.length} {t('LocationsWithCoordinates', { defaultValue: 'with coordinates' })}
                            </Typography>
                        )}
                    </Box>

                    <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={displayMode}
                            onChange={(_, value) => {
                                if (!value) return;
                                setDisplayMode(value);
                                setActiveMarker(null);
                                setSelectedPosition(null);
                                setMapCenter(GREECE_CENTER);
                            }}
                            sx={{
                                background: '#f4f6f8',
                                borderRadius: 8,
                                p: 0.3,
                                '& .MuiToggleButton-root': {
                                    border: 0,
                                    borderRadius: 8,
                                    px: 2,
                                    textTransform: 'none',
                                    fontWeight: 700
                                },
                                '& .Mui-selected': {
                                    background: '#fff !important',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                                }
                            }}
                        >
                            <ToggleButton value="list">{t('List')}</ToggleButton>
                            <ToggleButton value="grid">{t('Grid')}</ToggleButton>
                            <ToggleButton value="map">{t('Map')}</ToggleButton>
                        </ToggleButtonGroup>

                        <Tooltip title={t('Filters')}>
                            <Button variant="outlined" onClick={handleOpenFilters} sx={{ minWidth: 44, px: 1.2 }}>
                                <FilterAltOutlinedIcon />
                                {activeFilterCount ? (
                                    <Box component="span" sx={{ ml: 0.5, fontWeight: 800 }}>
                                        {activeFilterCount}
                                    </Box>
                                ) : null}
                            </Button>
                        </Tooltip>
                    </Stack>
                </Stack>

                {activeFilterCount > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {filters.search && (
                            <Chip label={`${t('Name')}: ${filters.search}`} onDelete={() => setFilters((current) => ({ ...current, search: '' }))} />
                        )}
                        {filters.country && (
                            <Chip
                                label={`${t('Country')}: ${filters.country}`}
                                onDelete={() => setFilters((current) => ({ ...current, country: '' }))}
                            />
                        )}
                        {filters.city && (
                            <Chip label={`${t('City')}: ${filters.city}`} onDelete={() => setFilters((current) => ({ ...current, city: '' }))} />
                        )}
                    </Stack>
                )}

                <Divider />

                {sitesQuery.isLoading && displayMode !== 'map' && (
                    <Stack spacing={2}>
                        {[1, 2, 3].map((item) => (
                            <Skeleton key={item} variant="rounded" height={220} sx={{ borderRadius: 3 }} />
                        ))}
                    </Stack>
                )}

                {!sitesQuery.isLoading && displayMode !== 'map' && filteredSites.length === 0 && (
                    <Box sx={{ p: 5, textAlign: 'center', borderRadius: 3, background: '#fff', border: '1px solid #e3e8ef' }}>
                        <Typography variant="h3" sx={{ mb: 1 }}>
                            {t('NoDataFound')}
                        </Typography>
                        <Typography color="text.secondary">
                            {t('TryChangingSearchTextOrFilters', { defaultValue: 'Try changing the search text or filters' })}
                        </Typography>
                    </Box>
                )}

                {!sitesQuery.isLoading && displayMode === 'list' && <Stack spacing={2}>{paginatedSites.map(renderListCard)}</Stack>}

                {!sitesQuery.isLoading && displayMode === 'grid' && (
                    <Grid container spacing={2}>
                        {paginatedSites.map(renderImageCard)}
                    </Grid>
                )}

                {displayMode === 'map' && renderMap()}

                {!sitesQuery.isLoading && displayMode !== 'map' && filteredSites.length > ITEMS_PER_PAGE && (
                    <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
                        <Pagination
                            color="primary"
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            showFirstButton
                            showLastButton
                        />
                    </Stack>
                )}
            </Stack>

            <Dialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{t('Filters')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            label={t('Name')}
                            value={draftFilters.search}
                            onChange={(event) => setDraftFilters((current) => ({ ...current, search: event.target.value }))}
                            fullWidth
                        />
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                gap: 2,
                                width: '100%'
                            }}
                        >
                            <TextField
                                select
                                label={t('Country')}
                                value={draftFilters.country}
                                onChange={(event) => setDraftFilters((current) => ({ ...current, country: event.target.value }))}
                                fullWidth
                            >
                                {countries.map((country) => (
                                    <MenuItem key={country} value={country}>
                                        {country}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                label={t('City')}
                                value={draftFilters.city}
                                onChange={(event) => setDraftFilters((current) => ({ ...current, city: event.target.value }))}
                                fullWidth
                            >
                                {cities.map((city) => (
                                    <MenuItem key={city} value={city}>
                                        {city}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClearFilters}>{t('Clear')}</Button>
                    <Button onClick={() => setIsFilterOpen(false)}>{t('Cancel')}</Button>
                    <Button variant="contained" onClick={handleApplyFilters}>
                        {t('Apply')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SitesView;
